import { useEffect, useRef, useState } from "react";
import api from "../api";

const fmtSize = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const FILTERS = [["tutti", "Tutti"], ["email", "📧 Email"], ["whatsapp", "💬 WhatsApp"], ["inviati", "📤 Inviati"], ["caricati", "📂 Caricati"]];

const triggerDownload = (blob, filename) => {
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = filename || "file";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 4000);
};
const isImg = (f) => (f.content_type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(f.filename || "");
const isPdf = (f) => (f.content_type || "").includes("pdf") || /\.pdf$/i.test(f.filename || "");
const isAudio = (f) => (f.content_type || "").startsWith("audio") || /\.(ogg|oga|opus|mp3|m4a|wav|amr)$/i.test(f.filename || "");
const hasPreview = (f) => isImg(f) || isPdf(f) || isAudio(f);
const typeIcon = (f) => {
  const n = (f.filename || "").toLowerCase();
  if (isPdf(f)) return "📄";
  if (isImg(f)) return "🖼️";
  if (isAudio(f)) return "🎵";
  if (/\.(xlsx?|csv|ods)$/.test(n)) return "📊";
  if (/\.(docx?|odt|rtf|txt)$/.test(n)) return "📝";
  if (/\.(zip|rar|7z)$/.test(n)) return "🗜️";
  if (/\.(xml|p7m)$/.test(n)) return "🧾";
  return "📄";
};

export default function ClientFiles({ companyId, personId }) {
  const [files, setFiles] = useState(null);
  const [sel, setSel] = useState({});
  const [filter, setFilter] = useState("tutti");
  const [limit, setLimit] = useState(40);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [prev, setPrev] = useState(null); // {f, url, loading, audioMissing}
  const [transcribing, setTranscribing] = useState(false);
  const [saveFor, setSaveFor] = useState(null); // file da salvare in cartella
  const [folderGroups, setFolderGroups] = useState(null);
  const [folderSel, setFolderSel] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [docs, setDocs] = useState(null);
  const [upBusy, setUpBusy] = useState(false);
  const fileRef = useRef(null);

  const docParam = companyId ? `company_id=${companyId}` : `person_id=${personId}`;
  const loadDocs = () => api.get(`/documents?${docParam}`).then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
  const uploadFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setUpBusy(true); setErr("");
    let ok = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append(companyId ? "company_id" : "person_id", companyId || personId);
        await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (er) { setErr(`Caricamento di "${file.name}" non riuscito: ` + (er.response?.data?.detail || er.message)); }
    }
    if (ok) { setFilter("caricati"); await loadDocs(); }
    setUpBusy(false);
  };
  const removeDoc = async (f) => {
    if (!window.confirm(`Eliminare "${f.filename}"?`)) return;
    try { await api.delete(`/documents/${f.id}`); loadDocs(); } catch (er) { setErr(er.response?.data?.detail || er.message); }
  };

  const openSave = async (f) => {
    setSaveFor(f); setFolderSel(""); setSaveMsg("");
    if (folderGroups === null) {
      try {
        const pr = await api.get(`/projects?company_id=${companyId}`);
        const dets = await Promise.all(pr.data.map((p) => api.get(`/projects/${p.id}`).then((r) => r.data).catch(() => null)));
        setFolderGroups(dets.filter(Boolean).map((d) => ({ id: d.project.id, name: d.project.name, folders: (d.folders || []).filter((x) => !x.closed) })).filter((g) => g.folders.length));
      } catch { setFolderGroups([]); }
    }
  };
  const doSave = async () => {
    setSaving(true); setSaveMsg("");
    try {
      if (saveFor.bulk) {
        const items = selectedSavable.map((f) => ({ kind: f.kind, id: f.id, index: f.index }));
        const r = await api.post("/emails/client-files/save-bulk", { items, company_id: companyId, folder_id: folderSel || null });
        const parts = [`✅ ${r.data.saved} salvati nella cartella "${r.data.folder_name}"`];
        if (r.data.already) parts.push(`${r.data.already} già presenti`);
        if (r.data.missing) parts.push(`${r.data.missing} non recuperabili`);
        setSaveMsg(parts.join(" · "));
      } else {
        const r = await api.post("/emails/client-files/save", {
          kind: saveFor.kind, id: saveFor.id, index: saveFor.index,
          company_id: companyId, folder_id: folderSel || null,
        });
        setSaveMsg(r.data.already ? `Già presente nella cartella "${r.data.folder_name}".` : `✅ Salvato nella cartella "${r.data.folder_name}".`);
      }
    } catch (e) { setSaveMsg("❌ " + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  const openPreview = async (f) => {
    setErr(""); setPrev({ f, url: null, loading: true });
    const url = f.kind === "doc" ? `/documents/${f.id}/download`
      : f.kind === "out" ? `/emails/sent/${f.id}/attachments/${f.index}?inline=1` : `/emails/${f.id}/attachments/${f.index}?inline=1`;
    try {
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: f.content_type || (isPdf(f) ? "application/pdf" : isAudio(f) ? "audio/mpeg" : "image/*") });
      setPrev({ f, url: URL.createObjectURL(blob), loading: false });
    } catch {
      if (isAudio(f) && f.transcription) {
        setPrev({ f, url: null, loading: false, audioMissing: true });
      } else {
        setPrev(null);
        setErr(`"${f.filename}" non è più recuperabile per l'anteprima.`);
      }
    }
  };
  const transcribeNow = async () => {
    if (!prev || prev.f.kind !== "in") return;
    setTranscribing(true);
    try {
      const r = await api.post(`/emails/${prev.f.id}/attachments/${prev.f.index}/transcribe`);
      const txt = r.data.transcription;
      setPrev((p) => ({ ...p, f: { ...p.f, transcription: txt } }));
      setFiles((list) => (list || []).map((x) => (x.kind === prev.f.kind && x.id === prev.f.id && x.index === prev.f.index ? { ...x, transcription: txt } : x)));
    } catch (e) { setErr("Trascrizione non riuscita: " + (e.response?.data?.detail || e.message)); }
    setTranscribing(false);
  };
  const closePreview = () => {
    if (prev?.url) setTimeout(() => URL.revokeObjectURL(prev.url), 2000);
    setPrev(null);
  };

  useEffect(() => {
    setFiles(null); setSel({}); setFilter("tutti"); setLimit(40); setErr(""); setQ(""); setDocs(null);
    if (!companyId && !personId) return;
    const param = companyId ? `company=${companyId}` : `person=${personId}`;
    api.get(`/emails/client-files?${param}`).then((r) => setFiles(r.data || [])).catch(() => setFiles([]));
    api.get(`/documents?${companyId ? `company_id=${companyId}` : `person_id=${personId}`}`).then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
  }, [companyId, personId]);

  const docRows = (docs || []).map((d) => ({
    kind: "doc", id: d.id, index: 0, filename: d.filename, size: d.size,
    content_type: d.content_type, date: d.created_at,
    who: d.source === "email" ? "Salvato da email" : "Caricato da te",
    subject: d.folder_id ? "in cartella" : "",
  }));
  const allRows = [...(files || []), ...docRows].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const key = (f) => `${f.kind}|${f.id}|${f.index}`;  const match = (f) => {
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      const hay = `${f.filename} ${f.subject} ${f.who} ${f.transcription || ""}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (filter === "tutti") return true;
    if (filter === "caricati") return f.kind === "doc";
    if (f.kind === "doc") return false;
    if (filter === "inviati") return f.kind === "out";
    if (filter === "whatsapp") return f.kind === "in" && f.channel === "whatsapp";
    return f.kind === "in" && f.channel !== "whatsapp";
  };
  const shown = allRows.filter(match);
  const selected = allRows.filter((f) => sel[key(f)]);
  const selectedSavable = selected.filter((f) => f.kind !== "doc");
  const selectable = shown;
  const allSel = selectable.length > 0 && selectable.every((f) => sel[key(f)]);
  const toggleAll = () => {
    const n = { ...sel };
    selectable.forEach((f) => { n[key(f)] = !allSel; });
    setSel(n);
  };

  const dlOne = async (f) => {
    setErr("");
    const url = f.kind === "doc" ? `/documents/${f.id}/download`
      : f.kind === "out" ? `/emails/sent/${f.id}/attachments/${f.index}` : `/emails/${f.id}/attachments/${f.index}`;
    try {
      const r = await api.get(url, { responseType: "blob" });
      triggerDownload(r.data, f.filename);
    } catch { setErr(`"${f.filename}" non è più scaricabile.`); }
  };

  const dlZip = async () => {
    setBusy(true); setErr("");
    try {
      const items = selected.map((f) => ({ kind: f.kind, id: f.id, index: f.index }));
      const r = await api.post("/emails/client-files/zip", { items }, { responseType: "blob" });
      triggerDownload(r.data, "file-cliente.zip");
    } catch { setErr("Download non riuscito: nessun contenuto disponibile per i file selezionati."); }
    setBusy(false);
  };

  return (
    <div className="panel" data-testid="ws-files">
      <div className="toolbar" style={{ marginTop: 0 }}>
        <h3 style={{ margin: 0 }}>📎 File del cliente {files && <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>({allRows.length})</span>}</h3>
        <div className="spacer" />
        <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={uploadFiles} data-testid="ws-files-upload-input" />
        <button className="btn sm primary" onClick={() => fileRef.current && fileRef.current.click()} disabled={upBusy}
          title="Carica file da tenere nella scheda del cliente (sempre scaricabili)" data-testid="ws-files-upload-btn">
          {upBusy ? "Carico…" : "⬆️ Carica file"}
        </button>
        {FILTERS.map(([f, lbl]) => (
          <button key={f} className={"btn sm" + (filter === f ? " primary" : "")} onClick={() => setFilter(f)} data-testid={`ws-files-filter-${f}`}>{lbl}</button>
        ))}
      </div>
      {!files && <p className="muted">Caricamento…</p>}
      {files && allRows.length === 0 && <p className="muted">Nessun file per questo cliente: carica il primo con «⬆️ Carica file».</p>}
      {files && allRows.length > 0 && (
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Cerca un file per nome, oggetto o mittente…"
          style={{ width: "100%", marginTop: 4 }} data-testid="ws-files-search" />
      )}
      {files && allRows.length > 0 && shown.length === 0 && <p className="muted" data-testid="ws-files-no-match">Nessun file corrisponde alla ricerca.</p>}
      {shown.length > 0 && (
        <div className="toolbar" style={{ marginTop: 4 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={allSel} onChange={toggleAll} data-testid="ws-files-select-all" />
            Seleziona tutti ({shown.length})
          </label>
          <div className="spacer" />
          {companyId && (
            <button className="btn sm" disabled={selectedSavable.length === 0 || saving} onClick={() => openSave({ bulk: true })} data-testid="ws-files-save-bulk">
              💾 Salva selezionati ({selectedSavable.length})
            </button>
          )}
          <button className="btn sm primary" disabled={selected.length === 0 || busy} onClick={dlZip} data-testid="ws-files-download-zip">
            {busy ? "Preparo lo zip…" : `⬇ Scarica selezionati (${selected.length})`}
          </button>
        </div>
      )}
      {err && <p style={{ color: "#fb7185", fontSize: 13 }} data-testid="ws-files-error">{err}</p>}
      {shown.slice(0, limit).map((f) => (
        <div key={key(f)} className="ws-file-row" data-testid={`ws-file-${f.kind}-${f.id}-${f.index}`}>
          <input type="checkbox" checked={!!sel[key(f)]} onChange={() => setSel((p) => ({ ...p, [key(f)]: !p[key(f)] }))} data-testid={`ws-file-check-${f.kind}-${f.id}-${f.index}`} />
          <span title={f.kind === "doc" ? "File caricato nella scheda" : f.kind === "out" ? "Inviato dallo Studio" : f.channel === "whatsapp" ? "Ricevuto via WhatsApp" : "Ricevuto via email"}>
            {f.kind === "doc" ? typeIcon(f) : f.kind === "out" ? "📤" : f.channel === "whatsapp" ? "💬" : "📥"}
          </span>
          <button className="ws-file-name" onClick={() => (hasPreview(f) ? openPreview(f) : dlOne(f))}
            title={hasPreview(f) ? `Anteprima ${f.filename}` : `Scarica ${f.filename}`}
            data-testid={`ws-file-dl-${f.kind}-${f.id}-${f.index}`}>
            {f.filename}
          </button>
          {hasPreview(f) && (
            <button className="btn sm" style={{ padding: "1px 7px", fontSize: 11 }} onClick={() => openPreview(f)}
              title={isAudio(f) ? "Ascolta / leggi trascrizione" : "Anteprima senza scaricare"}
              data-testid={`ws-file-preview-${f.kind}-${f.id}-${f.index}`}>{isAudio(f) ? "📝" : "👁"}</button>
          )}
          {companyId && f.kind !== "doc" && (
            <button className="btn sm" style={{ padding: "1px 7px", fontSize: 11 }} onClick={() => openSave(f)}
              title="Salva in una cartella del cliente" data-testid={`ws-file-save-${f.kind}-${f.id}-${f.index}`}>💾</button>
          )}
          {f.kind === "doc" && (
            <button className="btn sm danger" style={{ padding: "1px 7px", fontSize: 11 }} onClick={() => removeDoc(f)}
              title="Elimina questo file" data-testid={`ws-file-delete-${f.id}`}>🗑</button>
          )}
          <span className="muted" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{fmtSize(f.size)}</span>
          <span className="ws-file-meta muted" title={f.subject}>{f.who}{f.subject ? ` · ${f.subject}` : ""}</span>
          <span className="muted" style={{ fontSize: 11.5, whiteSpace: "nowrap" }}>{fmtDate(f.date)}</span>
        </div>
      ))}
      {shown.length > limit && (
        <button className="btn sm" style={{ marginTop: 8 }} onClick={() => setLimit((n) => n + 60)} data-testid="ws-files-more">Mostra altri</button>
      )}
      {prev && (
        <div className="modal-overlay" onClick={closePreview} data-testid="ws-file-preview-modal">
          <div className="modal" style={{ maxWidth: 900, width: "92vw" }} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closePreview} data-testid="ws-file-preview-close">✕</button>
            <h3 style={{ marginRight: 30, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {prev.f.kind === "doc" ? typeIcon(prev.f) : prev.f.kind === "out" ? "📤" : "📥"} {prev.f.filename}
            </h3>
            {prev.loading && <p className="muted">Caricamento anteprima…</p>}
            {prev.url && isImg(prev.f) && (
              <img src={prev.url} alt={prev.f.filename} style={{ maxWidth: "100%", maxHeight: "70vh", display: "block", margin: "0 auto", borderRadius: 8 }} />
            )}
            {prev.url && isPdf(prev.f) && (
              <iframe src={prev.url} title={prev.f.filename} style={{ width: "100%", height: "70vh", border: "1px solid var(--border)", borderRadius: 8, background: "#fff" }} />
            )}
            {isAudio(prev.f) && !prev.loading && (
              <div data-testid="ws-file-audio-preview">
                {prev.url
                  ? <audio controls src={prev.url} style={{ width: "100%" }} data-testid="ws-file-audio-player" />
                  : <p className="muted" style={{ fontSize: 13 }}>⚠️ L'audio non è più disponibile, ma la trascrizione è stata salvata.</p>}
                <div style={{ marginTop: 12 }}>
                  <b style={{ fontSize: 13 }}>📝 Trascrizione</b>
                  {prev.f.transcription
                    ? <p style={{ fontSize: 14, lineHeight: 1.6, background: "rgba(148,163,184,.08)", borderRadius: 8, padding: "10px 12px", userSelect: "text" }} data-testid="ws-file-transcription">“{prev.f.transcription}”</p>
                    : (
                      <p className="muted" style={{ fontSize: 13 }}>
                        Non ancora trascritto (i nuovi vocali vengono trascritti in automatico entro 30 minuti).{" "}
                        {prev.f.kind === "in" && prev.url && (
                          <button className="btn sm" onClick={transcribeNow} disabled={transcribing} data-testid="ws-file-transcribe-now">
                            {transcribing ? "Trascrizione in corso…" : "✍️ Trascrivi ora"}
                          </button>
                        )}
                      </p>
                    )}
                </div>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn" onClick={closePreview}>Chiudi</button>
              <button className="btn primary" onClick={() => dlOne(prev.f)} data-testid="ws-file-preview-download">⬇ Scarica</button>
            </div>
          </div>
        </div>
      )}
      {saveFor && (
        <div className="modal-overlay" onClick={() => setSaveFor(null)} data-testid="ws-file-save-modal">
          <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSaveFor(null)}>✕</button>
            <h3>💾 Salva in cartella</h3>
            <p className="muted" style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {saveFor.bulk ? `${selected.length} file selezionati` : saveFor.filename}
            </p>
            <select value={folderSel} onChange={(e) => setFolderSel(e.target.value)} style={{ width: "100%" }} data-testid="ws-file-save-folder">
              <option value="">📁 Bilancio {new Date().getFullYear()} (automatica)</option>
              {(folderGroups || []).map((g) => (
                <optgroup key={g.id} label={g.name}>
                  {g.folders.map((fo) => <option key={fo.id} value={fo.id}>{fo.name}</option>)}
                </optgroup>
              ))}
            </select>
            {folderGroups === null && <p className="muted" style={{ fontSize: 12 }}>Carico le cartelle…</p>}
            {saveMsg && <p style={{ fontSize: 13 }} data-testid="ws-file-save-msg">{saveMsg}</p>}
            <div className="modal-actions">
              <button className="btn" onClick={() => setSaveFor(null)}>Chiudi</button>
              <button className="btn primary" onClick={doSave} disabled={saving} data-testid="ws-file-save-confirm">
                {saving ? "Salvataggio…" : "Salva nella cartella"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
